import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  TablePagination,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import clientService from '../services/clientService';
import ClientForm from '../components/ClientForm';
import EditIcon from '@mui/icons-material/Edit';

interface Client {
  id: string;
  name: string;
}

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientService.getClients();
      // La API ahora devuelve un array de strings (nombres de clientes)
      const clientNames: string[] = response.data;
      const clientObjects: Client[] = clientNames.map(name => ({ id: name, name: name }));
      setClients(clientObjects);
      setError(null);
    } catch (err) {
      setError('Error al cargar los clientes.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleDeleteClient = async (clientName: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar este cliente?')) {
      try {
        await clientService.deleteClient(clientName);
        setClients(clients.filter(c => c.name !== clientName));
        setSuccessMessage('Cliente eliminado con éxito.');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error al eliminar el cliente.');
        console.error(err);
      }
    }
  };

  const handleFormClose = (clientModified: boolean) => {
    setOpenForm(false);
    setEditingClient(null);
    if (clientModified) {
      fetchClients();
      setSuccessMessage('Cliente guardado con éxito.');
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setOpenForm(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
 
  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedClients = filteredClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
 
  return (
    <React.Fragment>
      <Typography variant="h4" gutterBottom>
        Gestión de Clientes
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          variant="outlined"
          label="Filtrar por nombre"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: '300px' }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => { setEditingClient(null); setOpenForm(true); }}
          sx={{ fontWeight: 500, textTransform: 'none' }}
        >
          Añadir Cliente
        </Button>
      </Box>
 
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
 
      <Paper sx={{ height: 'calc(100vh - 280px)', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <TableContainer sx={{ flex: '1 1 auto', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedClients.length > 0 ? (
                    paginatedClients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>{client.name}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleEditClient(client)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteClient(client.name)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No se encontraron clientes.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredClients.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ flex: '0 0 auto' }}
            />
          </>
        )}
      </Paper>

      <ClientForm open={openForm} onClose={handleFormClose} client={editingClient} />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </React.Fragment>
  );
};

export default ClientsPage;